































































































































import Vue from "vue";
import VelloIcon from "@/components/vue-components/VelloIcon.vue";
import vIcon from "@/components/v-components/v-icon.vue";
import Controls from "@/components/vue-components/Controls.vue";
import RequestCustomerLink from "@/components/vue-components/RequestCustomerLink.vue";
import { Fetch } from "@/plugins/fetch";
import { getTimestamp } from "@/common/common";
import moment from "moment";
import HeaderDashboard from "@/components/vue-components/HeaderDashboard.vue";
import AppointmentsDashboard from "@/views/customer-dashboard/AppointmentsDashboard.vue";
import GiftcardsDashboard from "./GiftcardsDashboard.vue";
import {
  RecordObject,
  CompanyObject,
  ResourceObject,
  ServiceObject,
  OfficeObject,
  CustomerObject,
  ErrorObject
} from "./types/index";

export default Vue.extend({
  name: "CustomerDashboard",
  components: {
    vIcon,
    VelloIcon,
    Controls,
    RequestCustomerLink,
    HeaderDashboard,
    AppointmentsDashboard,
    GiftcardsDashboard
  },
  props: {
    email: {
      type: String,
      default: null
    },
    expire: {
      type: String,
      default: null
    },
    hash: {
      type: String,
      default: null
    },
    token: {
      type: String,
      default: null
    }
  },
  data: () => {
    return {
      status: "base" as string,
      timezone: moment.tz.guess() as string,
      baseUrl: process.env.VUE_APP_VELLO_URL as string,
      records: [] as RecordObject[],
      customerIds: [] as string[],
      companies: {} as Record<string, CompanyObject>,
      offices: {} as Record<string, OfficeObject>,
      resources: {} as Record<string, ResourceObject>,
      services: {} as Record<string, ServiceObject>,
      customers: [] as CustomerObject[],
      filteredFutureRecords: [] as RecordObject[],
      filteredPastRecords: [] as RecordObject[],
      errors: {} as ErrorObject,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      giftcards: [] as any[]
    };
  },
  computed: {
    getToken() {
      return this.$store.getters.GET_CSRF_INFORMATION.token;
    },
    isAppointments() {
      return this.$route.name === "CustomerDashboard";
    },
    isGiftcards() {
      return this.$route.name === "GiftcardsDashboard";
    }
  },
  methods: {
    async getUserData(): Promise<void> {
      await this.getCustomerIds();
      this.records = await this.getRecordsForAllCustomers();
      this.giftcards = await this.getGiftcardsForAllCustomers();

      await this.getRelatedEntitiesForRecords();
      this.sortRecordsFutureRecords(this.records);
      this.sortRecordsPastRecords(this.records);
    },

    async getCustomerIds(): Promise<void> {
      return Fetch.post(`customer/listcustomersbyemail`, this.getToken, {
        email: this.$props.email,
        expire: this.$props.expire,
        hash: this.$props.hash,
        token: this.$props.token,
        data: this.$props.data
      }).then(result => {
        if (!Object.prototype.hasOwnProperty.call(result, "ids"))
          throw new Error("IDs were not returned");
        this.customerIds = result.ids;
        this.customers = result.data;
      });
    },

    async getRecordsForAllCustomers(): Promise<RecordObject[]> {
      const recordFetchPromises = this.customerIds.map(customerId =>
        this.getRecordsForCustomer(customerId)
      );
      const records = await Promise.all(recordFetchPromises);
      return records
        .flat()
        .filter(
          r =>
            r.details.assetRecord === false &&
            r.confirmation_status === "confirmed"
        );
    },

    async getRecordsForCustomer(customerId: string): Promise<RecordObject[]> {
      return Fetch.get(`customer/${customerId}/records`, this.getToken, {
        email: this.$props.email,
        expire: this.$props.expire,
        hash: this.$props.hash,
        token: this.$props.token,
        from_time: moment()
          .subtract("2", "weeks")
          .unix(),
        to_time: moment()
          .add("6", "months")
          .unix(),
        limit: 100
      }).catch((err: Error) => {
        console.error("getRecordsForCustomer", err);
        return Promise.resolve([]);
      });
    },

    async getRelatedEntitiesForRecords(): Promise<void> {
      const companyIds: Set<string> = new Set();
      const officeIds: Set<string> = new Set();
      const resourceIds: Set<string> = new Set();
      const serviceIds: Set<string> = new Set();

      for (const record of this.records) {
        if (record.company) companyIds.add(record.company);
        if (record.office) officeIds.add(record.office);
        if (record.resource) resourceIds.add(record.resource);
        if (record.service) serviceIds.add(record.service);
      }

      await Promise.all([
        this.getCompanies(Array.from(companyIds)),
        this.getOffices(Array.from(officeIds)),
        this.getResources(Array.from(resourceIds)),
        this.getServices(Array.from(serviceIds))
      ]);

      for (const record of this.records) {
        if (this.companies[record.company]) {
          const company = this.companies[record.company] as CompanyObject;
          record.url = `${this.baseUrl}/${company.url}/viewrecord/${record.record_number}`;
        }
      }
    },

    async getCompanies(companyIds: string[]): Promise<void> {
      const companyFetchPromises = companyIds.map(companyId =>
        this.getCompany(companyId).catch(e => {
          console.error(`${e}: "Company not found"`);
          return null;
        })
      );

      const companies = await Promise.all(companyFetchPromises);
      companies.forEach(company => {
        if (company) {
          this.companies[company.id] = company;
        }
      });
    },

    async getOffices(officeIds: string[]): Promise<void> {
      const officeFetchPromises = officeIds.map(officeId =>
        this.getOffice(officeId).catch(e => {
          console.error(`${e}: "Office not found"`);
          return null;
        })
      );

      const offices = await Promise.all(officeFetchPromises);
      offices.forEach(office => {
        if (office) {
          this.offices[office.id] = office;
        }
      });
    },

    async getResources(resourceIds: string[]): Promise<void> {
      const resourceFetchPromises = resourceIds.map(resourceId =>
        this.getResource(resourceId).catch(e => {
          console.error(`${e}: "Resource not found"`);
          return null;
        })
      );

      const resources = await Promise.all(resourceFetchPromises);
      resources.forEach(resource => {
        if (resource) {
          this.resources[resource.id] = resource;
        }
      });
    },

    async getServices(serviceIds: string[]): Promise<void> {
      const serviceFetchPromises = serviceIds.map(serviceId =>
        this.getService(serviceId).catch(e => {
          console.error(`${e}: "Service not found"`);
          return null;
        })
      );

      const services = await Promise.all(serviceFetchPromises);
      services.forEach(service => {
        if (service) {
          this.services[service.id] = service;
        }
      });
    },

    async getCompany(companyId: string): Promise<CompanyObject> {
      return Fetch.get(`company/${companyId}`, this.getToken);
    },

    async getOffice(officeId: string): Promise<OfficeObject> {
      return Fetch.get(`office/${officeId}`, this.getToken);
    },

    async getResource(resourceId: string): Promise<ResourceObject> {
      return Fetch.get(`resource/${resourceId}`, this.getToken);
    },

    async getService(serviceId: string): Promise<ServiceObject> {
      return Fetch.get(`service/${serviceId}`, this.getToken);
    },

    sortRecordsFutureRecords(records: RecordObject[]) {
      const sortByTimeStart = records.sort(
        (objA, objB) => objA.allocated_time_start - objB.allocated_time_start
      );

      const futureSortedRecords = sortByTimeStart.filter(record => {
        return record.allocated_time_start > getTimestamp();
      });

      this.filteredFutureRecords = futureSortedRecords;
      return this.filteredFutureRecords;
    },

    sortRecordsPastRecords(records: RecordObject[]) {
      const sortByTimeStart = records.sort(
        (objA, objB) => objB.allocated_time_start - objA.allocated_time_start
      );

      const futureSortedRecords = sortByTimeStart.filter(record => {
        return record.allocated_time_start < getTimestamp();
      });

      this.filteredPastRecords = futureSortedRecords;
      return this.filteredPastRecords;
    },

    async getGiftcardsPerCustomer(customerId: string) {
      return Fetch.get(`customer/${customerId}/giftcards`, this.getToken, {
        email: this.$props.email,
        expire: this.$props.expire,
        hash: this.$props.hash,
        token: this.$props.token,
        // from_time: moment()
        //   .subtract("2", "weeks")
        //   .unix(),
        // to_time: moment()
        //   .add("6", "months")
        //   .unix(),
        limit: 100
      }).catch((err: Error) => {
        console.error("Get giftcards for customer", err);
        return [];
      });
    },

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getGiftcardsForAllCustomers(): Promise<any[]> {
      const giftcardFetchPromises = this.customerIds.map(customerId =>
        this.getGiftcardsPerCustomer(customerId)
      );
      const giftcards = await Promise.all(giftcardFetchPromises);
      return giftcards.flat();
    }
  },

  created() {
    if (
      !this.$props.email &&
      !this.$props.hash &&
      !this.$props.token &&
      !this.$props.expire
    ) {
      return;
    }
    if (
      !this.$props.email ||
      !this.$props.hash ||
      !this.$props.token ||
      !this.$props.expire
    ) {
      this.errors.missing_params = true;
      this.status = "error";
      return;
    }

    if (this.$props.expire < getTimestamp()) {
      this.errors.expired = true;
      this.status = "error";
      return;
    }

    this.status = "loading";

    this.getUserData()
      .then(() => {
        if (!this.records.length && !this.giftcards.length) {
          this.status = "no-records";
        } else {
          this.status = "done";
        }
      })
      .catch((err: Error) => {
        console.log("getUserData:", err);
        this.status = "done";
      });
  }
});
